import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, Inject, OnInit} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {DeliveryService} from '../../shared/service/delivery/delivery.service';
import {Observable, of, Subject, switchMap} from 'rxjs';
import {TranslateService, TranslatePipe} from '@ngx-translate/core';
import {ErrorInfo} from '../../shared/model/error-info';
import {environment} from '../../../../environments/environment';
import {DeliveryProductWithDeliveryDescription} from '../../shared/model/delivery-product-with-delivery-description';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {DeliveryType} from '../../shared/model/delivery-type';
import {ObtainablePipe} from '../../shared/pipes/obtainable.pipe';
import {ArrayToStringPipe} from '../../shared/pipes/array-to-string.pipe';
import {DatePrefixPipe} from '../../shared/pipes/date-prefix.pipe';
import {YesNoPipe} from '../../shared/pipes/yes-no.pipe';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {DeliveryBasesComponent} from '../delivery-bases/delivery-bases.component';
import {NgClass, AsyncPipe, DatePipe, NgTemplateOutlet} from '@angular/common';
import {MatCard} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {catchError, map} from 'rxjs/operators';
import {DeliveryDescription} from '../../shared/model/delivery-description';
import {MatButton, MatAnchor} from '@angular/material/button';
import '@datev/doc-viewer';
import {DacklBreakpoint, DacklBreakpointObserver} from '@datev/ngx-dackl';
import {DocumentStoreService} from 'commons';
import '@datev/dacks/content';
import {HtmlViewerComponent} from '../html-viewer/html-viewer.component';
import { FooterSpacing } from "../footer/footer-spacing";


interface BackButtonData {
  label: string;
  link: string;
}

@Component({
  selector: 'myupdates-delivery-product-details',
  templateUrl: './delivery-product-details.component.html',
  styleUrls: ['./delivery-product-details.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatButton, MatAnchor, MatIcon, MatCard, NgClass, DeliveryBasesComponent,
    ErrorBoxComponent, MatProgressSpinner, AsyncPipe, DatePipe, TranslatePipe, YesNoPipe, DatePrefixPipe,
    ArrayToStringPipe, ObtainablePipe, RouterLink, NgTemplateOutlet, HtmlViewerComponent,
    FooterSpacing]
})
export class DeliveryProductDetailsComponent implements OnInit {

  documentBuffer$?: Observable<{ buffer: ArrayBuffer, fileName: string } | null>;
  documentError$ = new Subject<boolean>();

  productWithDelivery$?: Observable<DeliveryProductWithDeliveryDescription>;
  errorInfo$: Observable<ErrorInfo>;
  contentError$ = new Subject<boolean>();
  dateTranslation = '';
  pdfAdditionalInfoText = '';

  protected readonly DeliveryType = DeliveryType;

  backButtonData$?: Observable<BackButtonData>;

  breakpoint$ = inject(DacklBreakpointObserver).breakpoint$;
  isSmallScreen$ = this.breakpoint$.pipe(map(breakpoint => breakpoint <= DacklBreakpoint.SM));

  constructor(private readonly route: ActivatedRoute,
              private readonly deliveryService: DeliveryService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly datePipe: DatePipe,
              private readonly documentStoreService: DocumentStoreService,
              private readonly translate: TranslateService,
              @Inject('Window') private readonly window: Window) {
    this.errorInfo$ = this.errorHandlingService.errorStream$;
  }

  ngOnInit(): void {
    this.pdfAdditionalInfoText = this.translate.instant('common.pdf_additional_info');
    const paramMap = this.route.snapshot.paramMap;
    const deliveryProductId = paramMap.get('product-id') ?? 'unknown';
    const deliveryId = paramMap.get('id') ?? 'unknown';
    this.productWithDelivery$ = this.errorHandlingService.bindErrorHandling(
      this.deliveryService.getDeliveryProductWithDeliveryDescriptionById(deliveryId, deliveryProductId));

    this.documentBuffer$ = this.productWithDelivery$.pipe(
      switchMap(productWithDelivery => {
        if (productWithDelivery?.product?.documentUrl) {
          return this.documentStoreService.getPdfBuffer(productWithDelivery?.product?.documentUrl).pipe(
            catchError(() => {
              this.documentError$.next(true);
              return of(null);
            }),
            map(buffer => buffer ? { buffer, fileName: `${crypto.randomUUID()}.pdf` } : null)
          );
        }
        return of(null);
      })
    );

    this.dateTranslation = this.translate.instant('common.date_format');

    this.backButtonData$ = this.productWithDelivery$.pipe(map(productWithDelivery => this.buildBackButton(productWithDelivery.deliveryDescription)));
  }

  private buildBackButton(deliveryDescription: DeliveryDescription): BackButtonData {
    return {
      label: this.buildDeliveryName(deliveryDescription),
      link: this.buildDeliveryLink(deliveryDescription)
    };
  }

  private buildDeliveryName(deliveryDescription: DeliveryDescription): string {
    switch (deliveryDescription.deliveryType) {
      case DeliveryType.MainRelease:
      case DeliveryType.CompactRelease:
        return deliveryDescription.deliveryName;
      default:
        return `${deliveryDescription.deliveryName} ${this.dateToString(deliveryDescription.deliveryDate)}`;
    }
  }

  private buildDeliveryLink(deliveryDescription: DeliveryDescription): string {
    switch (deliveryDescription.deliveryType) {
      case DeliveryType.MainRelease:
        return this.deliveryService.deliveryMainReleaseProductsRouteTemplate(deliveryDescription.id);
      case DeliveryType.CompactRelease:
        return '';
      default:
        return this.deliveryService.deliveryProductsRouteTemplate(deliveryDescription.id);
    }
  }

  private dateToString(date: Date): string {
    return this.datePipe.transform(date, this.dateTranslation) ?? '';
  }


  shareByMail(productWithDelivery: DeliveryProductWithDeliveryDescription | null): void {

    if (!productWithDelivery || !productWithDelivery.deliveryDescription || !productWithDelivery.product ||
      productWithDelivery.deliveryDescription.deliveryType === null || productWithDelivery.deliveryDescription.deliveryType === DeliveryType.CompactRelease) {
      return;
    }

    // Adobe Analytics Einbindung in erster Runde
    this.adobeTrackingService.publishClickEvent('Update-Termine:Detail:Teilen');

    let link = `${environment.entryPoint}delivery/`;
    switch (productWithDelivery.deliveryDescription.deliveryType) {
      case DeliveryType.MainRelease:
        link += `main-products/${productWithDelivery.deliveryDescription.id}/product/${productWithDelivery.product.id}`;
        break;
      default:
        link += `products/${productWithDelivery.deliveryDescription.id}/product/${productWithDelivery.product.id}`;
        break;
    }

    const mailSubject = `Leseempfehlung \\ ${productWithDelivery.product.title} \\ DATEV MyUpdates`;
    const mailBody = `Ich möchte Ihnen den folgenden Beitrag in DATEV MyUpdates empfehlen:` +
      `%0A${productWithDelivery.product.title}%0A${link}%0A%0A` +
      `Mit freundlichem Gruß`;

    this.window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
  }

  onBackButtonClick(): void {
    this.adobeTrackingService.publishClickEvent('Update-Termine:Detail:BackButton');
  }

}
